import { propertyLinkParameters } from '../../utils/property-link-template.util';
import { PropertySharingTextPart } from 'src/api/generated';
import { useState } from 'react';
import {
  PiArrowCounterClockwise,
  PiArrowDown,
  PiArrowUp,
  PiLinkSimple,
} from 'react-icons/pi';
import { PropertySharingDestination } from 'src/api/generated';
import { Link } from 'src/modules/shared/components/atoms/links/link';
import { IconBadge } from 'src/modules/shared/components/atoms/icon-badge';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { InlineSaveFeedback } from 'src/modules/shared/components/molecules/inline-save-feedback/inline-save-feedback';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  SettingPermission,
  PropertyPermission,
  WebsitePermission,
} from 'src/modules/shared/types/permissions.type';
import { useUnsavedNavigationGuard } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { usePropertyLinksSettings } from '../../hooks/use-property-links-settings';
import { PropertyLinkSetupModal } from './property-link-setup-modal';
import { PropertyLinkPrioritySkeleton } from '../molecules/property-link-priority-skeleton';

/** Configure the company fallback order and preview a structured external URL template. */
export function PropertyLinksSettingsSection() {
  const { t } = useI18n();
  const { hasPermission: canRead } = useHasPermissions(SettingPermission.Read);
  const { hasPermission: canUpdate } = useHasPermissions(
    SettingPermission.ManageTenant
  );
  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const { hasPermission: canReadWebsite } = useHasPermissions(
    WebsitePermission.Read
  );
  const canReadProperties = canReadAll || canReadScoped;
  const [confirmation, setConfirmation] = useState<'reset' | 'remove' | null>(
    null
  );
  const [editingExternal, setEditingExternal] = useState(false);
  const [modalDirty, setModalDirty] = useState(false);
  const links = usePropertyLinksSettings({
    canRead,
    canUpdate,
    canReadProperties,
    onSaved: () => {
      setConfirmation(null);
      setEditingExternal(false);
      setModalDirty(false);
    },
  });
  const { policy, validation: validated, invalidStoredValue } = links;
  const guard = useUnsavedNavigationGuard(modalDirty);
  const unavailable = (destination: PropertySharingDestination) =>
    destination === PropertySharingDestination.EXTERNAL
      ? !policy?.externalSite
      : destination === PropertySharingDestination.COMOS &&
        !links.comosWebsiteAvailable;
  const readOnly = !canUpdate || links.saving || links.loading;
  if (!canRead) return <Text>{t('propertyLinks.noPermission')}</Text>;
  const external = policy?.externalSite;
  const availableDestinations =
    policy?.priority.filter((destination) => !unavailable(destination)) ?? [];
  return (
    <>
      <InlineFormSection
        title={t('propertyLinks.title')}
        description={t('propertyLinks.description')}
        icon={<PiLinkSimple className="h-5 w-5" />}
        headerAction={
          canUpdate &&
          !links.loading &&
          !links.loadError &&
          links.canReset && (
            <Button
              size="sm"
              appearance="ghost"
              variant="neutral"
              title={t('propertyLinks.resetDefaultHelp')}
              disabled={readOnly || Boolean(links.refreshError)}
              onClick={() => {
                links.resetSaveFeedback();
                setConfirmation('reset');
              }}
            >
              <PiArrowCounterClockwise className="h-3.5 w-3.5" aria-hidden />
              {t('propertyLinks.resetDefault')}
            </Button>
          )
        }
      >
        <div className="space-y-6">
          {!links.loading &&
            (Boolean(links.refreshError) || links.loadError || !policy) && (
              <div role="alert">
                <Text>{t('propertyLinks.loadError')}</Text>
                <Button onClick={links.reload}>
                  {t('propertyLinks.retry')}
                </Button>
              </div>
            )}
          {!links.loading && invalidStoredValue && (
            <div role="alert">
              <Text>{t('propertyLinks.invalidSavedPolicy')}</Text>
            </div>
          )}
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Text as="h5" size="small" weight="medium">
                {t('propertyLinks.priority')}
              </Text>
              {!links.loading &&
                !editingExternal &&
                !confirmation &&
                !links.refreshError && (
                  <InlineSaveFeedback
                    saving={links.saving}
                    saved={links.saved}
                    error={
                      links.saveError
                        ? t('propertyLinks.autoSaveError')
                        : undefined
                    }
                    onRetry={canUpdate ? links.retrySave : undefined}
                  />
                )}
            </div>
            <Text size="small" color="secondary">
              {t('propertyLinks.priorityHelp')}
            </Text>
            <div aria-busy={links.loading}>
              {links.loading ? (
                <PropertyLinkPrioritySkeleton />
              ) : (
                !links.loadError &&
                policy && (
                  <div className="divide-y divide-secondary-200 rounded-lg border border-secondary-200">
                    {policy.priority.map((destination, index) => (
                      <div
                        key={destination}
                        className="flex items-start gap-3 p-3 sm:items-center"
                      >
                        <IconBadge
                          size="xs"
                          variant="neutral"
                          className="shrink-0"
                          icon={
                            unavailable(destination)
                              ? '–'
                              : availableDestinations.indexOf(destination) + 1
                          }
                        />
                        <div className="min-w-0 flex-1 space-y-1">
                          <Text
                            size="small"
                            weight="medium"
                            color={
                              unavailable(destination) ? 'secondary' : 'primary'
                            }
                          >
                            {t(`propertyLinks.destination.${destination}`)}
                            {unavailable(destination) &&
                              ` (${t('propertyLinks.unconfigured')})`}
                          </Text>
                          {destination === PropertySharingDestination.COMOS &&
                            canReadWebsite && (
                              <Link
                                href="/settings/websites"
                                external={false}
                                size="tiny"
                              >
                                {t('propertyLinks.configureComos')}
                              </Link>
                            )}
                          {destination ===
                            PropertySharingDestination.EXTERNAL && (
                            <>
                              {external && (
                                <Text
                                  size="tiny"
                                  color="secondary"
                                  disableAutoLink
                                  className="break-all"
                                >
                                  {external.origin}
                                </Text>
                              )}
                              <div className="flex flex-wrap gap-x-3 gap-y-1">
                                <Link
                                  mode="action"
                                  size="tiny"
                                  disabled={
                                    links.saving || (!external && readOnly)
                                  }
                                  onClick={() => {
                                    links.resetSaveFeedback();
                                    setEditingExternal(true);
                                  }}
                                >
                                  {t(
                                    external
                                      ? readOnly
                                        ? 'propertyLinks.viewExternal'
                                        : 'propertyLinks.editExternal'
                                      : 'propertyLinks.configureExternal'
                                  )}
                                </Link>
                                {external && (
                                  <Link
                                    mode="action"
                                    size="tiny"
                                    variant="danger"
                                    disabled={readOnly}
                                    onClick={() => {
                                      links.resetSaveFeedback();
                                      setConfirmation('remove');
                                    }}
                                  >
                                    {t('propertyLinks.removeExternal')}
                                  </Link>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button
                            size="sm"
                            appearance="outline"
                            variant="neutral"
                            isIconOnly
                            disabled={
                              readOnly ||
                              Boolean(links.refreshError) ||
                              index === 0
                            }
                            aria-label={`${t('propertyLinks.moveUp')} ${t(`propertyLinks.destination.${destination}`)}`}
                            onClick={() => links.moveDestination(index, -1)}
                          >
                            <PiArrowUp className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            size="sm"
                            appearance="outline"
                            variant="neutral"
                            isIconOnly
                            disabled={
                              readOnly ||
                              Boolean(links.refreshError) ||
                              index === policy.priority.length - 1
                            }
                            aria-label={`${t('propertyLinks.moveDown')} ${t(`propertyLinks.destination.${destination}`)}`}
                            onClick={() => links.moveDestination(index, 1)}
                          >
                            <PiArrowDown className="h-4 w-4" aria-hidden />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </section>
          {!links.loading &&
            !validated.success &&
            external?.origin &&
            propertyLinkParameters(external).length > 0 && (
              <div role="alert">
                <Text size="small" color="warning">
                  {t(
                    validated.error.issues.some(
                      (issue) => issue.path[1] === 'query'
                    )
                      ? 'propertyLinks.invalidQuery'
                      : 'propertyLinks.invalidTemplate'
                  )}
                </Text>
              </div>
            )}
        </div>
      </InlineFormSection>
      {editingExternal && (
        <PropertyLinkSetupModal
          site={
            external ?? {
              origin: '',
              path: [{ kind: PropertySharingTextPart.kind.TEXT, value: '/' }],
              query: [],
            }
          }
          identifiers={links.identifiers}
          disabled={readOnly}
          saving={links.saving}
          saveError={Boolean(links.saveError)}
          onDirtyChange={setModalDirty}
          onClose={() => {
            links.resetSaveFeedback();
            setEditingExternal(false);
            setModalDirty(false);
          }}
          onSave={links.saveExternalSite}
          identifierActions={
            <>
              {links.hasMoreIdentifiers && (
                <Button
                  appearance="outline"
                  loading={links.loadingMoreIdentifiers}
                  onClick={links.loadMoreIdentifiers}
                >
                  {t('propertyLinks.moreParameters')}
                </Button>
              )}
              {links.identifiersError && (
                <div>
                  <Text>{t('propertyLinks.parameterError')}</Text>
                  <Button onClick={links.reloadIdentifiers}>
                    {t('propertyLinks.retry')}
                  </Button>
                </div>
              )}
            </>
          }
        />
      )}
      <Modal
        isOpen={confirmation !== null}
        compact
        dismissible={!links.saving}
        onClose={() => {
          links.resetSaveFeedback();
          setConfirmation(null);
        }}
        header={
          <Text weight="medium">
            {t(
              confirmation === 'reset'
                ? 'propertyLinks.resetDefault'
                : 'propertyLinks.removeExternal'
            )}
          </Text>
        }
        body={
          <div className="space-y-3">
            <Text>
              {t(
                confirmation === 'reset'
                  ? 'propertyLinks.resetConfirmation'
                  : 'propertyLinks.removeExternalHelp'
              )}
            </Text>
            {links.saveError && (
              <div role="alert">
                <Text>{t('propertyLinks.autoSaveError')}</Text>
              </div>
            )}
          </div>
        }
        footer={
          <div className="flex justify-end gap-2 p-3">
            <Button
              appearance="ghost"
              variant="neutral"
              disabled={links.saving}
              onClick={() => {
                links.resetSaveFeedback();
                setConfirmation(null);
              }}
            >
              {t('propertyLinks.cancel')}
            </Button>
            <Button
              disabled={
                readOnly ||
                (confirmation === 'reset' && !links.hasDefaultPolicy)
              }
              loading={links.saving}
              onClick={() => {
                if (confirmation === 'reset') links.resetToDefault();
                else links.removeExternalSite();
              }}
            >
              {t(
                confirmation === 'reset'
                  ? 'propertyLinks.resetDefault'
                  : 'propertyLinks.removeExternal'
              )}
            </Button>
          </div>
        }
      />
      <UnsavedChangesModal
        isOpen={guard.isBlocked}
        onProceed={guard.proceed}
        onStay={guard.stay}
      />
    </>
  );
}
